import { Component, Input, SimpleChange } from "@angular/core";
import { MessageService } from "primeng/api";
import { Routes } from "src/app/shared/models/kpi-details";
import { RouteOptimizationModel } from "src/app/shared/models/RouteOptimizationModel";
import { RouteState, TBTabName } from "src/app/shared/entity/enums";
import { ErrorMessage, ErrorType } from "src/app/shared/entity/error.const";
import { ApproachType, OptimizationType, TransactionType } from "src/app/shared/entity/optimise";
import { DateFormatPipe, DateFormatType } from "src/app/shared/pipe/date-format-pipe/date-format.pipe";
import { RouteOptimizationService } from "src/app/shared/services/route-optimization/route-optimization.service";
import { OptimizationService } from "src/app/shared/services/optimization/optimization.service";
import { TaskInfoService } from "src/app/shared/services/task-info/task-info.service";
import { TaskMovementService } from "src/app/shared/services/operations/task-movement.service";

@Component({
    selector: "app-route-optimization",
    templateUrl: "./route-optimization.component.html",
    styleUrls: [
        "./route-optimization.component.scss",
        "../../../../shared/css/primeng-dropdown.component.scss",
        "../../../../shared/css/primeng-table.component.scss"
    ],
    standalone: false
})
export class RouteOptimizationComponent {
  @Input() routeDetails: Routes;
  routeOptimizationDetails: RouteOptimizationModel[] = [];
  routeOptimizationColumns = [
    { field: "last_optimized_time", header: "Last optimized time" },
    { field: "optimization_type", header: "Type of optimization" },
    { field: "optimization_service", header: "Optimization Service" },
  ];

  /** Optimization dialog state */
  showConfirmationDialog = false;
  showRPSOptimizationDialog = false;
  optimizationTitle = '';

  /** Approach selection */
  optimizationApproaches = [
    { label: 'No Restrictions', value: ApproachType.UNRESTRICTED },
    { label: 'According to vehicle', value: ApproachType.VEHICLE },
    { label: 'Curbside', value: ApproachType.CURB },
  ];
  selectedOptimizationApproach = ApproachType.VEHICLE;

  /**
   * Whether the route state disables optimization actions
   * (completed or onroute routes cannot be optimized).
   */
  get isRouteStateDisabled(): boolean {
    const state = this.routeDetails?.route_state?.toLowerCase() ?? '';
    return state === RouteState.Completed.toLowerCase() || state === RouteState.Onroute.toLowerCase();
  }

  /**
   * Whether the Slot in button should be disabled.
   * Disabled when route state is completed/onroute OR enable_slotin is false.
   */
  get isSlotInDisabled(): boolean {
    return this.isRouteStateDisabled || !this.routeDetails?.enable_slotin;
  }

  constructor(
    private routeOptimizationService: RouteOptimizationService,
    private dateFormatPipe: DateFormatPipe,
    private optimizationService: OptimizationService,
    private messageService: MessageService,
    private taskInfoService: TaskInfoService,
    private taskMovementService: TaskMovementService
  ) {}

  ngOnChanges(changes: SimpleChange): void {
    if (this.routeDetails && this.routeDetails?.route_id) {
      this.getRouteOptimization();
    }
  }

  getRouteOptimization() {
    this.routeOptimizationDetails = [];
    this.routeOptimizationService
      .getRouteOptimization(this.routeDetails?.route_id ?? "")
      .subscribe({
        next: (res: any) => {
          if(res) {
            this.routeOptimizationDetails.push({
              last_optimized_time: `${this.dateFormatPipe.transform(res?.last_optimized_time??'',DateFormatType.DATETIME)}`,
              optimization_type: res?.optimization_type,
              optimization_service:res?.optimization_service
            })
          }
        },
        error: (error: any) => {
          console.error(error);
        },
      });
  }

  /**
   * Opens the confirmation dialog for the selected optimization type.
   * @param type 'RPS Optimization' or 'Slot in'
   */
  openOptimizationConfirmation(type: string): void {
    this.optimizationTitle = type;
    this.selectedOptimizationApproach = ApproachType.VEHICLE;
    this.showConfirmationDialog = true;
  }

  /**
   * Handles the confirmation of the optimization and opens the
   * respective dialog or triggers slot-in based on the optimization type.
   */
  handleOptimizationConfirmation(): void {
    this.showConfirmationDialog = false;
    if (this.optimizationTitle === 'Slot in') {
      const payload = {
        routeId: this.routeDetails?.route_id || '',
        optimizationType: OptimizationType.SLOTIN,
        approachType: this.selectedOptimizationApproach,
        transactionType: TransactionType.COMMIT
      };
      this.getSlotInSequence(payload);
    }
    if (this.optimizationTitle === 'RPS Optimization') {
      this.showRPSOptimizationDialog = true;
    }
  }

  /**
   * Calls the OptimizationService to commit the slot-in sequence.
   * @param payload optimization request payload
   */
  getSlotInSequence(payload: any): void {
    this.optimizationService.getOptimizedSequence(payload).subscribe({
      next: (response: any) => {
        if (this.taskInfoService.selectedTabName.toLowerCase() === TBTabName.Planned.toLowerCase()) {
          this.taskMovementService.refereshTabsData();
        }
        this.messageService.add({
          severity: ErrorType.SUCCESS,
          closable: false,
          summary: "",
          detail: response?.message ?? ErrorMessage.SLOTINSUCCESS,
        });
      },
      error: (error: any) => {
        this.messageService.add({
          severity: ErrorType.ERROR,
          closable: false,
          summary: "",
          detail: error?.message ?? ErrorMessage.COMMITOPTIMISATIONERROR,
        });
      }
    });
  }
}
